import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {environment} from '../../../../../../../environments/environment';
import {TranslateService} from '@ngx-translate/core';

export interface PreviewTemplate {
    code: string;
    html: string;
    css: string;
    typescript: string;
}

type PreviewDevice = 'mobile' | 'tablette' | 'desktop';

/**
 * Messages échangés avec la landing page (`app-landingpage`, `builder/live-preview.ts`) : garder les deux
 * côtés alignés.
 */
type PreviewEvent =
    | { type: 'dmp-lp-preview-ready' }
    | { type: 'dmp-lp-preview-step'; step: number }
    | { type: 'dmp-lp-preview-built'; errors: string[] }
    | { type: 'dmp-lp-preview-error'; message: string };

const DEVICE_WIDTHS: Record<PreviewDevice, string> = {mobile: '375px', tablette: '768px', desktop: '100%'};
/** Même clé que les autres aperçus de l'application : le choix d'appareil les suit tous. */
const DEVICE_STORAGE_KEY = 'previewDisplayDevice';
/** Délai après la dernière frappe avant de reconstruire la page : une compilation par pause, pas par touche. */
const SEND_DELAY_MS = 300;
const MAX_ERRORS = 5;
/**
 * Attente de la page, une fois le cadre chargé, avant de la déclarer injoignable. Un cadre en erreur
 * (connexion refusée) déclenche lui aussi `load` : c'est l'absence du message « prête » qui le trahit.
 */
const READY_TIMEOUT_MS = 4000;

/**
 * Aperçu en direct d'un template de landing page. La landing page est chargée une fois dans un cadre
 * (`live=1`) ; chaque modification du code ou des données de test lui est envoyée par `postMessage`, et elle
 * se reconstruit sans recharger. Ce qui est affiché est donc le template en cours d'édition, pas la version
 * enregistrée.
 */
@Component({
    selector: 'dmp-lp-live-preview',
    templateUrl: './live-preview.component.html',
    styleUrls: ['./live-preview.component.scss'],
    standalone: false
})
export class LivePreviewComponent implements OnInit, OnChanges, OnDestroy {
    @Input() datastore: string;
    @Input() template: PreviewTemplate;
    @Input() data: Record<string, unknown>;
    @Input() steps: number[] = [];
    @Input() expanded = false;
    @Output() expandedChange = new EventEmitter<boolean>();

    @ViewChild('frame') frame: ElementRef<HTMLIFrameElement>;

    readonly modes = [
        {label: this.translate.instant('landingPage.template.edit.preview.renderMode'), value: 'render'},
        {label: this.translate.instant('landingPage.template.edit.preview.namesMode'), value: 'names'}
    ];
    readonly devices: { label: string; value: PreviewDevice; icon: string }[] = [
        {label: this.translate.instant('landingPage.template.edit.preview.deviceMobile'), value: 'mobile', icon: 'fas fa-mobile-alt'},
        {label: this.translate.instant('landingPage.template.edit.preview.deviceTablet'), value: 'tablette', icon: 'fas fa-tablet-alt'},
        {label: this.translate.instant('landingPage.template.edit.preview.deviceDesktop'), value: 'desktop', icon: 'fas fa-desktop'}
    ];

    /** `render` : la page telle qu'un visiteur la voit, avec les données de test ; `names` : le mode debug du moteur. */
    mode: 'render' | 'names' = 'render';
    step = 1;
    stepOptions: { label: string; value: number }[] = [];
    device: PreviewDevice = 'desktop';
    errors: string[] = [];
    src: SafeResourceUrl;
    /** La page chargée dans le cadre n'a pas répondu : serveur arrêté, ou version sans aperçu en direct. */
    unreachable = false;
    baseUrl = environment.landingPagePreviewBaseUrl;
    /** En développement, la landing page est un serveur à part (`npm run serve:app-landingpage`, port 4201). */
    readonly production = environment.production;

    private url: string;
    private origin: string;
    private ready = false;
    private sendTimer: ReturnType<typeof setTimeout>;
    private readyTimer: ReturnType<typeof setTimeout>;

    private readonly onMessage = (event: MessageEvent) => {
        if (!this.frame || event.source !== this.frame.nativeElement.contentWindow) {
            return;
        }
        const message = event.data as PreviewEvent;
        switch (message?.type) {
            case 'dmp-lp-preview-ready':
                this.ready = true;
                this.unreachable = false;
                clearTimeout(this.readyTimer);
                this.send();
                break;
            case 'dmp-lp-preview-step':
                // Un bouton de la page a changé d'étape : le choix suit, sans reconstruire.
                this.step = message.step;
                break;
            case 'dmp-lp-preview-built':
                this.errors = message.errors.slice(0, MAX_ERRORS);
                break;
            case 'dmp-lp-preview-error':
                if (!this.errors.includes(message.message) && this.errors.length < MAX_ERRORS) {
                    this.errors = [...this.errors, message.message];
                }
                break;
        }
    };

    constructor(private sanitizer: DomSanitizer, private readonly translate: TranslateService) {
    }

    get width(): string {
        return DEVICE_WIDTHS[this.device];
    }

    ngOnInit(): void {
        this.device = this.readDevice();
        window.addEventListener('message', this.onMessage);
        if (environment.landingPagePreviewBaseUrl && this.datastore) {
            // `pageHtml` n'est pas relu en mode `live` (le template arrive par message), mais le moteur
            // n'accepte les messages que dans une page d'aperçu, qui en porte un.
            const code = encodeURIComponent(this.template?.code || 'HTML5_PREVIEW');
            this.url = `${environment.landingPagePreviewBaseUrl}/${encodeURIComponent(this.datastore)}/TEST?pageHtml=${code}&lang=FR&live=1`;
            this.origin = new URL(this.url, window.location.href).origin;
            this.src = this.sanitizer.bypassSecurityTrustResourceUrl(this.url);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.steps) {
            this.stepOptions = (this.steps ?? []).map(step => ({label: this.translate.instant('landingPage.template.edit.preview.step', {step}), value: step}));
            if (this.steps?.length && !this.steps.includes(this.step)) {
                this.step = this.steps[0];
            }
        }
        if (changes.template || changes.data || changes.steps) {
            clearTimeout(this.sendTimer);
            this.sendTimer = setTimeout(() => this.send(), SEND_DELAY_MS);
        }
    }

    ngOnDestroy(): void {
        clearTimeout(this.sendTimer);
        clearTimeout(this.readyTimer);
        window.removeEventListener('message', this.onMessage);
    }

    /** Envoie le template et les données à la page, qui se reconstruit. */
    send(): void {
        clearTimeout(this.sendTimer);
        const target = this.frame?.nativeElement.contentWindow;
        if (!this.ready || !target || !this.template) {
            return;
        }
        target.postMessage({
            type: 'dmp-lp-preview',
            template: {...this.template},
            rawData: this.data ?? {},
            debug: this.mode === 'names',
            step: this.step
        }, this.origin);
    }

    /** Recharge la page : utile si un script du template l'a laissée dans un état incohérent. */
    reload(): void {
        if (!this.frame) {
            return;
        }
        this.ready = false;
        this.unreachable = false;
        this.errors = [];
        this.frame.nativeElement.src = this.url;
    }

    /** Le cadre a fini de charger : la page doit s'annoncer peu après, sinon elle n'est pas joignable. */
    onFrameLoad(): void {
        clearTimeout(this.readyTimer);
        if (!this.ready) {
            this.readyTimer = setTimeout(() => this.unreachable = !this.ready, READY_TIMEOUT_MS);
        }
    }

    toggleExpanded(): void {
        this.expanded = !this.expanded;
        this.expandedChange.emit(this.expanded);
    }

    saveDevice(): void {
        try {
            localStorage.setItem(DEVICE_STORAGE_KEY, this.device);
        } catch {
            // Stockage indisponible (navigation privée) : le choix vaut pour cette page seulement.
        }
    }

    private readDevice(): PreviewDevice {
        try {
            const value = localStorage.getItem(DEVICE_STORAGE_KEY) as PreviewDevice;
            return value in DEVICE_WIDTHS ? value : 'desktop';
        } catch {
            return 'desktop';
        }
    }
}
